import React, { useState, useRef, useEffect } from "react";
import reportIcon from "../icons/report.png";
import pdfIcon from "../icons/pdf.png";
import imgIcon from "../icons/img.png";
import { ApplyModal } from "./StudentApplicationScreen";
import { collection, onSnapshot, query, where, addDoc, serverTimestamp, doc, getDoc } from "firebase/firestore";
import { standingOf } from "./accountStanding";
import { db } from "./firebase";
import { useVisibleOjtPosts } from "./useVisibleOjtPosts";
import { color, font, type, space, radius, shadow, ease } from "./theme";

// ── Design tokens, aliased for this screen ────────────────────────────────────
// Kapareho ng CoordinatorFindCompanyScreen — lahat galing sa theme.js.
// Walang hardcoded hex dito; kung magbabago ang palette, sa theme.js lang.
const ink        = color.ink;          // primary text on light surfaces
const inkBody    = color.inkBody;      // body copy
const inkMuted   = color.inkMuted;     // secondary/meta text
const inkFaint   = color.inkFaint;     // placeholders, empty states
const surface    = color.wine600;      // cards
const page       = color.wine900;      // page background
const field      = color.wine700;      // inputs / neutral fills
const line       = color.wine700;      // hairlines & borders
const lineSoft   = color.wine800;
const panel      = color.blush100;     // dark panels (header bar, modal footer)
const panelDeep  = color.blush50;
const onPanel    = color.onWine;
const onPanelDim = color.onWineMuted;
const danger     = color.danger;
const success    = color.success;

const CLOUDINARY_CLOUD_NAME    = "doalndt5l";
const CLOUDINARY_UPLOAD_PRESET = "ojtern_docs";

const MAPBOX_TOKEN = "pk.eyJ1IjoibWFraWlpaS0iLCJhIjoiY21wbTgybHVmMmc1ZzJycTFuZXRlb3NoNCJ9.FIpjF2lKTHkbU1e6qrL_Pw";

// ── Mapbox read-only map view (shown in company/post profile) ─────────────────
const MapboxStaticView = ({ lat, lng, address }) => {
  const mapContainer = useRef(null);
  const mapRef       = useRef(null);
  const [showZoom, setShowZoom] = useState(false);

  useEffect(() => {
    if (!mapContainer.current || mapRef.current) return;
    if (!lat || !lng) return;

    const link = document.createElement("link");
    link.rel  = "stylesheet";
    link.href = "https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.css";
    document.head.appendChild(link);

    const initMap = () => {
      const mapboxgl = window.mapboxgl;
      mapboxgl.accessToken = MAPBOX_TOKEN;
      const map = new mapboxgl.Map({
        container: mapContainer.current,
        style: "mapbox://styles/mapbox/streets-v12",
        center: [lng, lat],
        zoom: 15,
        interactive: true,
      });
      map.addControl(new mapboxgl.NavigationControl(), "top-right");
      new mapboxgl.Marker({ color: danger }).setLngLat([lng, lat]).addTo(map);
      mapRef.current = map;
    };

    if (window.mapboxgl) { initMap(); return; }
    const script = document.createElement("script");
    script.src = "https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.js";
    script.onload = initMap;
    document.head.appendChild(script);
  }, [lat, lng]);

  if (!lat || !lng) {
    return (
      <div style={{ width: "100%", height: "100%", minHeight: "200px", borderRadius: radius.card, background: field, border: `1px solid ${line}`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: space.sm }}>
        <svg width="26" height="32" viewBox="0 0 24 30" fill={inkFaint}><path d="M12 0C7.58 0 4 3.58 4 8c0 5.25 8 16 8 16s8-10.75 8-16c0-4.42-3.58-8-8-8zm0 11c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z"/></svg>
        <span style={{ fontFamily: font.ui, ...type.helper, color: inkMuted, textAlign: "center", padding: `0 ${space.md}` }}>{address || "No location set"}</span>
      </div>
    );
  }

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <div ref={mapContainer} style={{ width: "100%", height: "100%", borderRadius: radius.card, overflow: "hidden", border: `1px solid ${line}` }} />
      <button
        onClick={() => setShowZoom(true)}
        title="Open the full map"
        style={{
          position: "absolute", bottom: "10px", left: "50%", transform: "translateX(-50%)",
          background: panelDeep, color: onPanel, border: "none", borderRadius: radius.pill,
          padding: "6px 14px", fontFamily: font.ui, ...type.helper, fontWeight: 500,
          cursor: "pointer", zIndex: 5, boxShadow: shadow.pill,
        }}
      >
        Open full map
      </button>
      {showZoom && <MapZoomModal lat={lat} lng={lng} onClose={() => setShowZoom(false)} />}
    </div>
  );
};

// ── Fullscreen map modal ──────────────────────────────────────────────────────
const MapZoomModal = ({ lat, lng, onClose }) => {
  const mapContainerRef = useRef(null);
  const mapRef          = useRef(null);

  useEffect(() => {
    const loadMap = () => {
      if (!mapContainerRef.current || mapRef.current) return;
      window.mapboxgl.accessToken = MAPBOX_TOKEN;
      mapRef.current = new window.mapboxgl.Map({
        container: mapContainerRef.current,
        style:     "mapbox://styles/mapbox/standard-satellite",
        center:    [lng, lat],
        zoom:      15,
      });
      mapRef.current.addControl(new window.mapboxgl.NavigationControl(), "top-right");
      new window.mapboxgl.Marker({ color: danger }).setLngLat([lng, lat]).addTo(mapRef.current);
    };

    if (window.mapboxgl) { loadMap(); return; }
    const link = document.createElement("link");
    link.rel  = "stylesheet";
    link.href = "https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.css";
    document.head.appendChild(link);
    const script = document.createElement("script");
    script.src = "https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.js";
    script.onload = loadMap;
    document.head.appendChild(script);

    return () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } };
  }, [lat, lng]);

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(10,10,10,0.55)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: space.md }}
    >
      <div
        onClick={e => e.stopPropagation()}
        className="stud-map-zoom-inner"
        style={{ borderRadius: radius.panel, overflow: "hidden", position: "relative", boxShadow: shadow.panel }}
      >
        <div ref={mapContainerRef} style={{ width: "100%", height: "100%" }} />
        <button
          onClick={onClose}
          style={{ position: "absolute", top: space.md, left: space.md, zIndex: 10, background: panel, color: onPanel, border: "none", borderRadius: radius.pill, padding: "8px 18px", fontFamily: font.ui, ...type.control, cursor: "pointer", boxShadow: shadow.pill }}
        >
          Close
        </button>
      </div>
    </div>
  );
};

// ─── SUCCESS MODAL ────────────────────────────────────────────────────────────
const SuccessModal = ({ onClose }) => (
  <div
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(10,10,10,0.5)",
      zIndex: 2000,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: space.md,
    }}
  >
    <div
      style={{
        background: surface,
        borderRadius: radius.panel,
        padding: `${space.xl} ${space.lg}`,
        textAlign: "center",
        maxWidth: "380px",
        border: `1px solid ${line}`,
        boxShadow: shadow.panel,
      }}
    >
      <div style={{ width: "56px", height: "56px", borderRadius: "50%", background: lineSoft, display: "flex", alignItems: "center", justifyContent: "center", margin: `0 auto ${space.md}` }}>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={success} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
      <h3 style={{ fontFamily: font.ui, fontSize: "1.25rem", fontWeight: 600, letterSpacing: "-0.01em", color: ink, marginBottom: space.sm }}>Report sent</h3>
      <p style={{ fontFamily: font.ui, ...type.body, color: inkBody, marginBottom: space.lg }}>The review team will look into it and get back to you here.</p>
      <button
        onClick={onClose}
        style={{
          background: panel,
          color: onPanel,
          border: "none",
          borderRadius: radius.pill,
          padding: "11px 34px",
          fontFamily: font.ui,
          ...type.control,
          cursor: "pointer",
          transition: `background 240ms ${ease}`,
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = panelDeep)}
        onMouseLeave={(e) => (e.currentTarget.style.background = panel)}
      >
        Done
      </button>
    </div>
  </div>
);

// ─── INDUSTRY OPTIONS ───────────────────────────────────────
// Nothing is hardcoded here anymore — this used to be an empty INDUSTRIES array,
// so the filter had no options to show at all. The list is built from the
// industry each company actually typed at Sign-Up Step 1 (IndustryAutocomplete
// there); CompanyCreatePostScreen copies that onto every post it saves, so a
// newly registered industry turns up in the filter on its own, and no option
// can ever come back with zero results.
const industriesOf = (c) =>
  (Array.isArray(c?.industry) ? c.industry : (c?.industry ? [c.industry] : []))
    .map(i => String(i).trim())
    .filter(Boolean);

// De-duplicated case-insensitively (Industry is free-typed, so casing drifts)
// and sorted A→Z, the same way Step 1 builds its own suggestion list.
const buildIndustryOptions = (posts = []) => {
  const seen = new Map();
  posts.forEach(p => industriesOf(p).forEach(ind => {
    const key = ind.toLowerCase();
    if (!seen.has(key)) seen.set(key, ind);
  }));
  return [...seen.values()].sort((a, b) => a.localeCompare(b));
};

// ─── POST EXPIRATION ─────────────────────────────────────────────
// Same rules as CompanyCreatePostScreen, which writes the field: the date is a
// plain "YYYY-MM-DD" string so it compares lexicographically without timezone
// drift, a post only counts as expired the day AFTER its expirationDate (set
// Sept 9 and Sept 9 itself is still open), and an empty value means the post
// never expires.
const getTodayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const isPostExpired = (post) => !!post?.expirationDate && post.expirationDate < getTodayStr();

// Built from the date parts instead of new Date("YYYY-MM-DD") — that parses as
// UTC midnight and would render a day early anywhere behind UTC.
const formatDateStr = (str) => {
  const [y, m, d] = String(str || "").split("-").map(Number);
  if (!y || !m || !d) return "";
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

// ─── ALL COMPANIES (kept for legacy import compatibility — use useOjtPosts hook instead) ──
export const ALL_COMPANIES = [];

// ─── HOOK: fetch live OJT posts from Firestore ────────────────────────────────
export const useOjtPosts = () => {
  // Live posts, trimmed to programs the company is still APPROVED for (a
  // withdrawn program's post never reaches this screen) and with the posts of
  // BLOCKED companies removed. See useVisibleOjtPosts.js / useEligibleOjtPosts.js.
  const { posts, loading } = useVisibleOjtPosts();
  return { posts, loading };
};

// ─── REPORT CATEGORIES ────────────────────────────────────────────────────────
// Input ceilings for the report form.
const REPORT_DESCRIPTION_MAX = 1500;

const reportCategories = [
  { label: "Fraud and Scam", description: "Job scams are fraudulent schemes where scammers impersonate employers to steal money, personal information, or coerce victims into fake work activities.", details: ["Fake job postings requiring payment", "Identity theft", "Misrepresentation of company"] },
  { label: "Discrimination", description: "Discrimination in the workplace involves unfair treatment of individuals based on race, gender, age, religion, disability, or other protected characteristics.", details: ["Racial discrimination", "Gender-based bias", "Age discrimination", "Religious intolerance"] },
  { label: "Sexual Harassment", description: "Sexual harassment includes any unwelcome sexual advances, requests for sexual favors, or other verbal or physical conduct of a sexual nature in the workplace.", details: ["Unwanted physical contact", "Verbal harassment", "Hostile work environment", "Quid pro quo harassment"] },
  { label: "Harmful Misinformation", description: "Spreading false information about OJT programs, company practices, or student requirements that can mislead or harm students.", details: ["False program descriptions", "Fake requirements", "Misleading slot information"] },
  { label: "Workplace Misconduct", description: "Workplace misconduct refers to behavior that violates company policies or professional standards, including unsafe working conditions.", details: ["Unsafe working conditions", "Violation of OJT agreement", "Forced overtime", "Unpaid work"] },
  { label: "Others", description: "Any other concern not listed above. Please provide a detailed description of the issue.", details: [] },
];

// ── Responsive styles injected once ───────────────────────────────────────────
const ResponsiveStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap');

    /* Company grid: 2-col ≥768px, 1-col below — NO horizontal scroll */
    .stud-company-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: ${space.md};
    }
    @media (max-width: 767px) {
      .stud-company-grid { grid-template-columns: 1fr; }
    }

    /* Search bar shrinks on mobile */
    .stud-search-input { width: 170px; }
    .stud-search-input::placeholder { color: ${inkFaint}; }
    @media (max-width: 480px) {
      .stud-search-input { width: 90px; }
    }
    @media (max-width: 380px) {
      .stud-search-input { width: 62px; }
    }

    /* Visible keyboard focus on every control in this screen */
    .stud-list-wrapper :focus-visible,
    .stud-profile-content :focus-visible,
    .stud-modal :focus-visible {
      outline: none;
      box-shadow: ${shadow.focus};
      border-radius: ${radius.pill};
    }

    .stud-map-zoom-inner {
      width: min(80vw, 620px);
      height: min(70vh, 440px);
    }
    @media (max-width: 560px) {
      .stud-map-zoom-inner {
        width: calc(100vw - 72px);
        height: 46vh;
      }
    }

    /* Company profile content.
       Column layout: header/map row + hairline stay their natural height and
       the Post Details box takes ALL the space between that hairline and the
       action bar. The action bar is a sibling below (no longer an overlay),
       so the Post Details box — and the help-tour highlight around it —
       stops exactly at the bar's top border and never covers Apply / Message
       / Report. */
    .stud-profile-content {
      display: flex;
      flex-direction: column;
      padding: 28px 32px 20px;
    }
    @media (max-width: 640px) {
      .stud-profile-content { padding: 16px 16px 16px; }
    }
    .stud-profile-content > .stud-profile-top,
    .stud-profile-content > .stud-profile-rule { flex: 0 0 auto; }

    .stud-profile-details {
      flex: 1 1 0;
      min-height: 220px;   /* if the screen is very short, the page scrolls instead */
      overflow-y: auto;
      padding-right: 4px;
      -webkit-overflow-scrolling: touch;
    }

    /* Profile top row: side-by-side on desktop, stacked on mobile */
    .stud-profile-top {
      display: flex;
      gap: ${space.lg};
      margin-bottom: ${space.lg};
    }
    @media (max-width: 640px) {
      .stud-profile-top { flex-direction: column; }
    }

    /* Map: full width on mobile */
    .stud-map-box {
      width: 320px;
      height: 260px;
      flex-shrink: 0;
    }
    @media (max-width: 640px) {
      .stud-map-box { width: 100%; height: 240px; }
    }

    /* Profile bottom bar — a normal flex sibling under the scroll area */
    .stud-profile-bar { padding: 14px 32px; flex-shrink: 0; }
    @media (max-width: 640px) {
      .stud-profile-bar { padding: 12px 16px; }
    }

    .stud-btn-apply   { padding: 12px 28px; }
    .stud-btn-message { padding: 12px 24px; }

    .stud-action-buttons {
      display: flex;
      gap: ${space.sm};
      min-width: 0;
    }
    /* Mobile: Apply Now on top, Message Now underneath (same width), and the
       Report button stays on the side. Less cramped than three in a row. */
    @media (max-width: 640px) {
      .stud-action-buttons { flex-direction: column; align-items: stretch; }
      .stud-action-buttons button { text-align: center; }
      .stud-btn-apply, .stud-btn-message { padding: 10px 22px; }
    }

    /* Apply Now modal (ApplyModal, imported from StudentApplicationScreen).
       Styled from here too, so Find Company always shows:
       1. the same slim mobile width as the Report modal, and
       2. the selected dropdown row in the active theme's tint — the modal
          hardcodes a pink (#f0e5e5) inline, which is what turned red on
          every theme. */
    @media (max-width: 560px) {
      .sa-modal-inner { width: calc(100vw - 72px) !important; }
    }
    .sa-modal-inner [style*="rgb(240, 229, 229)"],
    .sa-modal-inner [style*="#f0e5e5"] {
      background: ${color.hoverWashStrong} !important;
    }

    /* Report modal card: slimmer on mobile so it doesn't hug the screen edges —
       same width as the "Open full map" modal (.stud-map-zoom-inner). */
    .stud-report-card {
      width: 100%;
      max-width: 540px;
    }
    @media (max-width: 560px) {
      .stud-report-card { width: calc(100vw - 72px); }
    }

    /* Search+filter bar layout */
    .stud-search-bar {
      padding: 18px 22px;
      margin-bottom: ${space.lg};
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: ${space.md};
      flex-wrap: nowrap;
    }
    @media (max-width: 480px) {
      .stud-search-bar { padding: 14px; gap: 10px; }
    }

    /* List wrapper: vertical scroll only, no horizontal overflow */
    .stud-list-wrapper {
      overflow-x: hidden;
      width: 100%;
    }

    @media (prefers-reduced-motion: reduce) {
      .stud-card { transition: none !important; }
    }
  `}</style>
);

// ── Alert Modal (replaces native window.alert with an in-app styled dialog) ──
const AlertModal = ({ message, onClose }) => (
  <div className="stud-modal" style={{ position: "fixed", inset: 0, background: "rgba(10,10,10,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100, padding: space.md }}>
    <div style={{ background: surface, borderRadius: radius.card, width: "100%", maxWidth: "340px", overflow: "hidden", textAlign: "center", border: `1px solid ${line}`, boxShadow: shadow.panel }}>
      <div style={{ padding: `${space.lg} ${space.lg} ${space.md}` }}>
        <p style={{ fontFamily: font.ui, ...type.body, color: inkBody, margin: 0 }}>{message}</p>
      </div>
      <button onClick={onClose} style={{ width: "100%", padding: "13px", border: "none", borderTop: `1px solid ${line}`, background: surface, color: ink, fontFamily: font.ui, ...type.control, cursor: "pointer" }}>OK</button>
    </div>
  </div>
);

// ── Placement Modal: shown when Apply Now is tapped after a placement is confirmed ──
const PlacementConfirmedModal = ({ company, onClose }) => (
  <div className="stud-modal" style={{ position: "fixed", inset: 0, background: "rgba(10,10,10,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000, padding: space.md }}>
    <div style={{ background: surface, borderRadius: radius.panel, padding: `${space.xl} ${space.lg}`, textAlign: "center", width: "100%", maxWidth: "380px", border: `1px solid ${line}`, boxShadow: shadow.panel }}>
      <div style={{ width: "56px", height: "56px", borderRadius: "50%", background: lineSoft, display: "flex", alignItems: "center", justifyContent: "center", margin: `0 auto ${space.md}` }}>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={success} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
      <h3 style={{ fontFamily: font.ui, fontSize: "1.25rem", fontWeight: 600, letterSpacing: "-0.01em", color: ink, marginBottom: space.sm }}>You have been applied</h3>
      <p style={{ fontFamily: font.ui, ...type.body, color: inkBody, marginBottom: space.lg }}>
        You already have a confirmed OJT placement at {company}.
      </p>
      <button
        onClick={onClose}
        style={{ background: panel, color: onPanel, border: "none", borderRadius: radius.pill, padding: "11px 34px", fontFamily: font.ui, ...type.control, cursor: "pointer", transition: `background 240ms ${ease}` }}
        onMouseEnter={(e) => (e.currentTarget.style.background = panelDeep)}
        onMouseLeave={(e) => (e.currentTarget.style.background = panel)}
      >
        OK
      </button>
    </div>
  </div>
);

// ─── REPORT MODAL ─────────────────────────────────────────────────────────────
const ReportModal = ({ company, onClose, onSubmit, reporter }) => {
  const [step, setStep]                 = useState(1);
  const [selected, setSelected]         = useState(null);
  const [description, setDescription]   = useState("");
  const [attachedFiles, setAttachedFiles] = useState([]); // [{ name, type, file (raw) }]
  const [submitting, setSubmitting]     = useState(false);
  const [submitError, setSubmitError]   = useState("");
  const [alertMsg, setAlertMsg]         = useState("");
  const fileRef = useRef();

  const handleFile = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    if (!picked.length) return;
    if (picked.some(f => !["image/png", "application/pdf"].includes(f.type))) { setAlertMsg("That file type isn't supported. Attach PNG or PDF files only."); return; }
    const MAX_TOTAL = 10 * 1024 * 1024; // 10MB combined
    const current = attachedFiles.reduce((sum, a) => sum + (a.file?.size || 0), 0);
    const added   = picked.reduce((sum, f) => sum + f.size, 0);
    if (current + added > MAX_TOTAL) { setAlertMsg("Your files can't add up to more than 10MB in total."); return; }
    setAttachedFiles(prev => [...prev, ...picked.map(file => ({ name: file.name, type: file.type, file }))]);
  };

  const uploadToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);
    formData.append("folder", "ojtern_reports");
    const res = await fetch(`https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/upload`, {
      method: "POST",
      body: formData,
    });
    if (!res.ok) throw new Error("The file didn't upload. Try again.");
    const data = await res.json();
    return { url: data.secure_url, name: file.name, type: file.type };
  };

  const handleSubmit = async () => {
    if (!description.trim()) { setAlertMsg("Add a description of what happened."); return; }
    if (attachedFiles.length === 0)       { setAlertMsg("Attach a file that supports your report."); return; }
    setSubmitting(true);
    setSubmitError("");
    try {
      const filesData = await Promise.all(attachedFiles.map(a => uploadToCloudinary(a.file)));

      const reportDoc = {
        // See the identical comment in CoordinatorFindCompanyScreen.jsx —
        // `company` is an ojt_posts document here, so `.id` is the POST's
        // doc id, not the actual company's. Use the post's own `companyId`
        // field (the real owner uid) instead.
        companyId:      company.companyId || company.id || company.uid || "",
        company:        company.companyName || company.name || "",
        // Always a company here — this modal is opened from a company's post.
        subjectType:    "company",
        subjectId:      company.companyId || company.id || company.uid || "",
        subjectName:    company.companyName || company.name || "",
        concern:        selected?.label || "Others",
        description,
        attachedFile:   filesData[0] || null, // first file, kept for older readers
        attachedFiles:  filesData,
        reportedBy:     reporter?.uid  || "",
        reporterName:   reporter?.name || reporter?.fullName || "Unknown",
        reporterRole:   "student",
        date:           new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
        createdAt:      serverTimestamp(),
        status:         "pending",
      };

      await addDoc(collection(db, "reports"), reportDoc);
      onSubmit?.({ ...reportDoc, attachedFile: filesData[0] || null, attachedFiles: filesData });
      onClose();
    } catch (err) {
      setSubmitError(err.message || "The report didn't send. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const cat = reportCategories.find(c => c.label === selected?.label);

  return (
    <div className="stud-modal" style={{ position: "fixed", inset: 0, background: "rgba(10,10,10,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: space.md }}>
      <div className="stud-report-card" style={{ background: surface, borderRadius: radius.panel, maxHeight: "86vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: shadow.panel }}>
        <div style={{ padding: `${space.md} ${space.lg}`, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${line}` }}>
          <div>
            <span style={{ fontFamily: font.ui, fontSize: "1.125rem", fontWeight: 600, letterSpacing: "-0.01em", color: ink }}>Report this company</span>
            <p style={{ fontFamily: font.ui, ...type.helper, color: inkMuted, marginTop: "2px" }}>Step {step} of 3</p>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", fontSize: "1.15rem", cursor: "pointer", color: inkMuted, lineHeight: 1 }}>✕</button>
        </div>

        {/* Progress hairline — three segments, one per step */}
        <div id="sreport-progress" style={{ display: "flex", gap: "3px", padding: `0 ${space.lg}`, marginTop: "10px" }}>
          {[1, 2, 3].map(n => (
            <div key={n} style={{ flex: 1, height: "3px", borderRadius: radius.pill, background: n <= step ? ink : line, transition: `background 260ms ${ease}` }} />
          ))}
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: `${space.md} ${space.lg} ${space.lg}` }}>
          {step === 1 && (
            <div id="sreport-concerns">
              <p style={{ fontFamily: font.ui, ...type.label, color: ink, marginBottom: "12px" }}>What is the concern?</p>
              {reportCategories.map((c) => {
                const isOn = selected?.label === c.label;
                return (
                  <div key={c.label} onClick={() => setSelected(c)} style={{ display: "flex", alignItems: "center", gap: "12px", padding: "12px 0", cursor: "pointer", borderBottom: `1px solid ${lineSoft}` }}>
                    <div style={{ width: "20px", height: "20px", borderRadius: "50%", border: `1.5px solid ${isOn ? ink : color.wine400}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, background: isOn ? ink : surface, transition: `all 180ms ${ease}` }}>
                      {isOn && <div style={{ width: "7px", height: "7px", borderRadius: "50%", background: color.white }} />}
                    </div>
                    <span style={{ fontFamily: font.ui, ...type.body, color: isOn ? ink : inkBody }}>{c.label}</span>
                  </div>
                );
              })}
            </div>
          )}
          {step === 2 && cat && (
            <div id="sreport-details">
              <p style={{ fontFamily: font.ui, fontSize: "1rem", fontWeight: 600, color: ink, marginBottom: space.sm }}>{cat.label}</p>
              <p style={{ fontFamily: font.ui, ...type.body, color: inkBody, marginBottom: space.md, maxWidth: "62ch" }}>{cat.description}</p>
              {cat.details.length > 0 && (
                <>
                  <p style={{ fontFamily: font.ui, ...type.label, color: ink, marginBottom: space.sm }}>Common forms this takes</p>
                  <ul style={{ paddingLeft: "18px", margin: 0 }}>
                    {cat.details.map((d, i) => <li key={i} style={{ fontFamily: font.ui, ...type.helper, color: inkBody, marginBottom: space.xs }}>{d}</li>)}
                  </ul>
                </>
              )}
            </div>
          )}
          {step === 3 && (
            <>
              <div id="sreport-describe">
              <p style={{ fontFamily: font.ui, ...type.label, color: ink, marginBottom: space.sm }}>Describe what happened</p>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value.slice(0, REPORT_DESCRIPTION_MAX))}
                maxLength={REPORT_DESCRIPTION_MAX}
                placeholder="Include dates, names, and anything the review team should see."
                style={{ width: "100%", minHeight: "112px", border: `1px solid ${line}`, borderRadius: radius.card, padding: "12px 14px", outline: "none", fontFamily: font.ui, ...type.body, resize: "vertical", background: color.wine800, color: ink, display: "block", boxSizing: "border-box" }}
              />
              <p style={{ fontFamily: font.ui, ...type.helper, color: description.length >= REPORT_DESCRIPTION_MAX ? "#c00" : inkMuted, textAlign: "right", margin: `4px 2px ${space.lg} 0` }}>
                {description.length}/{REPORT_DESCRIPTION_MAX}
              </p>
              </div>
              <div id="sreport-evidence">
              <p style={{ fontFamily: font.ui, ...type.label, color: ink, marginBottom: "4px" }}>Attach evidence</p>
              <p style={{ fontFamily: font.ui, ...type.helper, color: inkMuted, marginBottom: "10px" }}>PNG or PDF, up to 10MB in total. You can attach more than one file.</p>
              <input ref={fileRef} type="file" accept=".png,.pdf" multiple style={{ display: "none" }} onChange={handleFile} />
              {attachedFiles.length > 0 && (
                <div style={{ maxHeight: "132px", overflowY: "auto", overflowX: "hidden", padding: "8px 8px 2px 0", display: "flex", flexWrap: "wrap", gap: "10px", alignContent: "flex-start", marginBottom: "10px" }}>
                  {attachedFiles.map((a, idx) => (
                    <div key={idx} style={{ position: "relative", width: "84px", display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
                      <img src={/^image\//i.test(a.type || "") ? imgIcon : pdfIcon} alt="" style={{ width: "52px", height: "60px", objectFit: "contain", display: "block" }} />
                      <span title={a.name} style={{ fontFamily: font.ui, fontSize: "0.68rem", color: inkBody, textAlign: "center", wordBreak: "break-all", maxWidth: "84px", lineHeight: 1.3 }}>{a.name}</span>
                      <button onClick={() => setAttachedFiles(prev => prev.filter((_, i) => i !== idx))} aria-label={`Remove ${a.name}`} style={{ position: "absolute", top: "-6px", right: "6px", width: "20px", height: "20px", borderRadius: "50%", background: color.wine800, border: `1px solid ${line}`, cursor: "pointer", color: inkMuted, fontSize: "0.85rem", lineHeight: 1, padding: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>&times;</button>
                    </div>
                  ))}
                </div>
              )}
              <button onClick={() => fileRef.current.click()} style={{ display: "flex", alignItems: "center", gap: "10px", background: color.wine800, border: `1px dashed ${color.wine400}`, borderRadius: radius.card, padding: "12px 18px", cursor: "pointer", fontFamily: font.ui, ...type.control, color: ink }}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={inkMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                </svg>
                {attachedFiles.length > 0 ? "Add more files" : "Choose files"}
              </button>
              </div>
            </>
          )}
        </div>

        <div style={{ background: panel, padding: `12px ${space.lg}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: space.md }}>
          <div style={{ minWidth: 0 }}>
            {submitError
              ? <p style={{ fontFamily: font.ui, ...type.helper, color: "#E8A5A2", margin: 0 }}>{submitError}</p>
              : <p style={{ fontFamily: font.ui, ...type.helper, color: onPanelDim, margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{company.companyName || company.name}</p>}
          </div>
          <div id="sreport-actions" style={{ display: "flex", gap: space.sm, flexShrink: 0 }}>
            {step > 1 && (
              <button
                onClick={() => setStep(step - 1)}
                style={{ padding: "9px 18px", borderRadius: radius.pill, background: "transparent", color: onPanelDim, border: `1px solid ${color.onWineFaint}`, fontFamily: font.ui, ...type.control, cursor: "pointer" }}
              >
                Back
              </button>
            )}
            {step < 3 ? (
              <button
                onClick={() => { if (step === 1 && !selected) { setAlertMsg("Pick a concern to continue."); return; } setStep(step + 1); }}
                style={{ padding: "9px 22px", borderRadius: radius.pill, background: color.white, color: ink, border: "none", fontFamily: font.ui, ...type.control, cursor: "pointer" }}
              >
                Continue
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={submitting}
                style={{ padding: "9px 22px", borderRadius: radius.pill, background: color.white, color: ink, border: "none", fontFamily: font.ui, ...type.control, cursor: submitting ? "not-allowed" : "pointer", opacity: submitting ? 0.6 : 1 }}
              >
                {submitting ? "Sending…" : "Send report"}
              </button>
            )}
          </div>
        </div>
      </div>
      {alertMsg && <AlertModal message={alertMsg} onClose={() => setAlertMsg("")} />}
    </div>
  );
};

// ── Small section heading used across the profile ─────────────────────────────
const SectionTitle = ({ children }) => (
  <h2 style={{ fontFamily: font.ui, fontSize: "1.0625rem", fontWeight: 600, letterSpacing: "-0.01em", color: ink, marginBottom: space.sm }}>{children}</h2>
);

// ─── COMPANY PROFILE ──────────────────────────────────────────────────────────
const CompanyProfile = ({ company, onBack, onReport, onMessageNow, onApplyNow }) => {
  const loc = company.location || {};
  const locationParts = [loc.street, loc.barangay, loc.city, loc.province, loc.region].filter(Boolean);
  const fullLocation = loc.fullAddress || locationParts.join(", ");
  const locationLines = [
    loc.region   ? ["Region", loc.region]             : null,
    loc.province ? ["Province", loc.province]         : null,
    loc.city     ? ["City or municipality", loc.city] : null,
    loc.barangay ? ["Barangay", loc.barangay]         : null,
    loc.street   ? ["Street or building", loc.street] : null,
  ].filter(Boolean);

  // Per-department slot rows. `courseSelections` is the only shape that carries
  // a per-department `slot` (written by Create Post); `departments` is the
  // legacy department-only fallback, which has no count of its own.
  const deptSlotRows = Array.isArray(company.courseSelections) && company.courseSelections.length
    ? company.courseSelections
    : (Array.isArray(company.departments) ? company.departments.map(d => ({ college: d })) : []);

  const bodyStyle = { fontFamily: font.ui, ...type.body, color: inkBody, maxWidth: "68ch" };

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", background: page, overflow: "hidden", position: "relative" }}>
      <div className="stud-profile-content" style={{ flex: 1, overflowY: "auto" }}>

        {/* Top row: description + map */}
        <div className="stud-profile-top">
          <div style={{ flex: 1, minWidth: 0 }}>
          {/* Inner wrapper = help-tour target, width: fit-content so the
              highlight hugs Back + name + description (grows with them). */}
          <div id="sprofile-details" style={{ width: "fit-content", maxWidth: "100%" }}>
            <button
              onClick={onBack}
              style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "none", border: "none", cursor: "pointer", fontFamily: font.ui, ...type.helper, color: inkMuted, padding: 0, marginBottom: "10px" }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
              Back
            </button>
            <h1 style={{ fontFamily: font.ui, fontSize: "clamp(1.5rem, 4vw, 2rem)", fontWeight: 600, lineHeight: 1.15, letterSpacing: "-0.02em", color: ink, marginBottom: "10px" }}>{company.companyName || company.name}</h1>
            <p style={bodyStyle}>{company.description}</p>
          </div>
          </div>
          <div id="sprofile-map" className="stud-map-box" style={{ borderRadius: radius.card, overflow: "hidden" }}>
            <MapboxStaticView
              lat={company.postLocation?.lat || company.location?.lat}
              lng={company.postLocation?.lng || company.location?.lng}
              address={company.postLocation?.address || fullLocation}
            />
          </div>
        </div>

        <hr className="stud-profile-rule" style={{ border: "none", borderTop: `1px solid ${line}`, margin: `0 0 ${space.lg}` }} />

        {/* Requirements → Skills required as one scroll box that fills the space
            between the hairline above and the action bar below (see
            .stud-profile-details). The help tour's "Post Details" highlight
            therefore covers only this stretch — not the Apply / Message /
            Report bar. */}
        <div id="sprofile-details-full" className="stud-profile-details">
        <SectionTitle>Requirements:</SectionTitle>
        <p style={{ ...bodyStyle, whiteSpace: "pre-line", marginBottom: space.lg }}>{Array.isArray(company.requirements) ? company.requirements.join("\n") : (company.requirements || "Not listed")}</p>

        <SectionTitle>Working hours:</SectionTitle>
        <p style={{ ...bodyStyle, marginBottom: space.lg, whiteSpace: "pre-line" }}>{company.workingHours}</p>

        <SectionTitle>Contact:</SectionTitle>
        <p style={{ ...bodyStyle, marginBottom: space.xs }}>Phone: {company.phone || company.contact?.phone || "Not listed"}</p>
        <p style={{ ...bodyStyle, marginBottom: space.lg }}>Email: {company.contactEmail || company.contact?.email || company.email || "Not listed"}</p>

        <SectionTitle>Location:</SectionTitle>
        <div style={{ marginBottom: space.lg }}>
          {locationLines.length > 0 ? (
            locationLines.map(([k, v], i) => (
              <p key={i} style={{ ...bodyStyle, marginBottom: "3px" }}>
                <span style={{ color: inkMuted }}>{k}: </span>{v}
              </p>
            ))
          ) : (
            <p style={bodyStyle}>{company.postLocation?.address || "No location set"}</p>
          )}
        </div>

        <SectionTitle>Benefits:</SectionTitle>
        <p style={{ ...bodyStyle, whiteSpace: "pre-line", marginBottom: space.lg }}>{Array.isArray(company.benefits) ? company.benefits.join("\n") : (company.benefits || "Not listed")}</p>

        {/* Open to these programs — slots are per department/program now (see
            ApprovedDepartmentPicker in CompanyCreatePostScreen), so each count
            sits beside the department it actually belongs to instead of one
            combined number for the whole post. */}
        <SectionTitle>Open to these programs:</SectionTitle>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: space.sm, marginBottom: space.lg }}>
          {deptSlotRows.length === 0 && <p style={bodyStyle}>Not listed</p>}
          {deptSlotRows.map((cp, i) => {
            const label = [cp.college, cp.program, cp.specialization].filter(Boolean).join(" · ");
            const deptSlot = Number(cp.slot) || 0;
            return (
              <span
                key={i}
                style={{ display: "inline-flex", alignItems: "center", gap: space.sm, maxWidth: "100%", padding: "5px 5px 5px 14px", borderRadius: radius.pill, background: color.wine800, border: `1px solid ${line}`, color: inkBody, fontFamily: font.ui, ...type.helper }}
              >
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
                <span style={{ flexShrink: 0, padding: "2px 10px", borderRadius: radius.pill, background: surface, border: `1px solid ${line}`, color: deptSlot > 0 ? success : inkMuted, fontWeight: 500, whiteSpace: "nowrap" }}>
                  {deptSlot > 0 ? `${deptSlot} slot${deptSlot !== 1 ? "s" : ""}` : "No slot set"}
                </span>
              </span>
            );
          })}
        </div>

        {/* Industry sits under the programs block, not up in the header — it's the
            company detail a reader checks after seeing which departments the
            post is open to. Value comes from Sign-Up Step 1 and is copied onto
            the post by CompanyCreatePostScreen. */}
        <SectionTitle>Industry:</SectionTitle>
        <p style={{ ...bodyStyle, marginBottom: space.lg }}>{industriesOf(company).join(", ") || "Not listed"}</p>

        <SectionTitle>Skills required:</SectionTitle>
        <p style={{ ...bodyStyle, whiteSpace: "pre-line" }}>{Array.isArray(company.skillsRequired) ? company.skillsRequired.join("\n") : (company.skillsRequired || company.skills?.join(", ") || "Not listed")}</p>
        </div>
      </div>

      {/* Bottom action bar */}
      <div className="stud-profile-bar" style={{ background: surface, borderTop: `1px solid ${line}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: space.md }}>
        <div className="stud-action-buttons">
          {/* With a confirmed placement the click is handled by the parent, which shows a modal instead of the apply form. */}
          <button
            id="sprofile-apply-btn"
            className="stud-btn-apply"
            onClick={onApplyNow}
            style={{ background: panel, color: onPanel, border: "none", borderRadius: radius.pill, fontFamily: font.ui, ...type.control, cursor: "pointer", boxShadow: shadow.pill, transition: `background 240ms ${ease}`, whiteSpace: "nowrap" }}
            onMouseEnter={e => (e.currentTarget.style.background = panelDeep)}
            onMouseLeave={e => (e.currentTarget.style.background = panel)}
          >
            Apply Now!
          </button>
          <button
            id="sprofile-message-btn"
            className="stud-btn-message"
            onClick={onMessageNow}
            style={{ background: surface, color: ink, border: `1px solid ${color.wine400}`, borderRadius: radius.pill, fontFamily: font.ui, ...type.control, cursor: "pointer", transition: `background 240ms ${ease}`, whiteSpace: "nowrap" }}
            onMouseEnter={e => (e.currentTarget.style.background = color.hoverWash)}
            onMouseLeave={e => (e.currentTarget.style.background = surface)}
          >
            Message Now!
          </button>
        </div>
        <button
          id="sprofile-report-btn"
          onClick={onReport}
          title="Report this company"
          style={{ display: "inline-flex", alignItems: "center", gap: space.sm, background: "transparent", border: `1px solid ${line}`, borderRadius: radius.pill, padding: "9px 16px", cursor: "pointer", fontFamily: font.ui, ...type.helper, color: inkMuted, flexShrink: 0, whiteSpace: "nowrap" }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = color.hoverBorder; e.currentTarget.style.color = ink; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = line; e.currentTarget.style.color = inkMuted; }}
        >
          <img src={reportIcon} alt="" style={{ width: "18px", height: "18px", objectFit: "contain", opacity: 0.75 }} />
          Report
        </button>
      </div>
    </div>
  );
};

// ─── FILTER PANEL ─────────────────────────────────────────────────────────────
const FilterPanel = ({ selectedIndustries, setSelectedIndustries, citySearch, setCitySearch, industryOptions }) => {
  // Industry is a type-to-search field now, the same interaction as Sign-Up
  // Step 1's IndustryAutocomplete: type, pick a suggestion, and it becomes a
  // chip you can click off. The old version painted every industry as a pill at
  // once, which stops working the moment companies start typing their own.
  const [industryQuery, setIndustryQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);

  const toggleIndustry = (ind) =>
    setSelectedIndustries(prev => prev.includes(ind) ? prev.filter(i => i !== ind) : [...prev, ind]);

  const clearAll = () => { setSelectedIndustries([]); setCitySearch(""); setIndustryQuery(""); };

  const q = industryQuery.trim().toLowerCase();
  const options = industryOptions || [];
  const matches = options.filter(ind => !selectedIndustries.includes(ind) && (q === "" || ind.toLowerCase().includes(q)));

  const fieldStyle = { width: "100%", padding: "9px 14px", borderRadius: radius.pill, border: `1px solid ${line}`, background: color.wine800, ...type.helper, fontFamily: font.ui, outline: "none", boxSizing: "border-box", color: ink };

  return (
    <div style={{ position: "absolute", top: "48px", right: 0, width: "250px", background: surface, border: `1px solid ${line}`, borderRadius: radius.card, boxShadow: shadow.panel, zIndex: 100, overflow: "hidden", fontFamily: font.ui }}>
      <div style={{ padding: "12px 14px 6px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: space.sm }}>
          <p style={{ ...type.label, color: ink }}>Industry</p>
          <button onClick={clearAll} style={{ background: "none", border: "none", ...type.helper, color: inkMuted, cursor: "pointer", fontFamily: font.ui, padding: 0, textDecoration: "underline" }}>Clear all</button>
        </div>

        <input
          type="text"
          value={industryQuery}
          onChange={e => { setIndustryQuery(e.target.value); setShowSuggestions(true); }}
          onFocus={() => setShowSuggestions(true)}
          placeholder="Type an industry"
          autoComplete="off"
          style={fieldStyle}
        />

        {showSuggestions && (
          <div style={{ marginTop: "8px", maxHeight: "130px", overflowY: "auto", border: `1px solid ${lineSoft}`, borderRadius: radius.card }}>
            {matches.length === 0 ? (
              <p style={{ ...type.helper, color: inkMuted, fontFamily: font.ui, padding: "8px 12px", margin: 0 }}>
                {options.length === 0 ? "No industries posted yet." : "No industry matches that."}
              </p>
            ) : matches.map(ind => (
              <div
                key={ind}
                onClick={() => { toggleIndustry(ind); setIndustryQuery(""); setShowSuggestions(false); }}
                style={{ padding: "7px 12px", cursor: "pointer", fontFamily: font.ui, ...type.helper, color: inkBody }}
                onMouseEnter={e => (e.currentTarget.style.background = color.hoverWash)}
                onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
              >
                {ind}
              </div>
            ))}
          </div>
        )}

        {selectedIndustries.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "8px" }}>
            {selectedIndustries.map(ind => (
              <span
                key={ind}
                onClick={() => toggleIndustry(ind)}
                title="Remove"
                style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: "4px 11px", borderRadius: radius.pill, ...type.helper, fontFamily: font.ui, cursor: "pointer", userSelect: "none", background: ink, color: color.white, border: `1px solid ${ink}`, transition: `all 160ms ${ease}` }}
              >
                {ind}<span style={{ opacity: 0.7 }}>✕</span>
              </span>
            ))}
          </div>
        )}
      </div>

      <hr style={{ border: "none", borderTop: `1px solid ${lineSoft}`, margin: "10px 0" }} />

      <div style={{ padding: "0 14px 14px" }}>
        <p style={{ ...type.label, color: ink, marginBottom: space.sm }}>Location</p>
        <input
          type="text"
          value={citySearch}
          onChange={e => setCitySearch(e.target.value)}
          placeholder="City, province, or region"
          style={{ width: "100%", padding: "9px 14px", borderRadius: radius.pill, border: `1px solid ${line}`, background: color.wine800, ...type.helper, fontFamily: font.ui, outline: "none", boxSizing: "border-box", color: ink }}
        />
      </div>
    </div>
  );
};

// ─── COMPANY CARD ─────────────────────────────────────────────────────────────
const CompanyCard = ({ company, onViewProfile }) => {
  // An expired post can't be applied to, so it shouldn't open either: the card
  // greys out and stops responding, the same as a disabled post. It stays in
  // the list (with its "Expired" date) rather than vanishing, so a student who
  // remembers seeing it can tell what happened.
  const postExpired = isPostExpired(company);
  // Strict on purpose: a post with disabled:true but no `active` field used to slip
  // through `active !== false` and stay clickable. Disabled OR expired = closed.
  const isActive = company.disabled !== true && company.active !== false && !postExpired;
  const displayName = company.companyName || company.name || "Unnamed company";
  const displayIndustry = industriesOf(company).join(", ") || "—";
  const displayLocation = typeof company.location === "object"
    ? [company.location?.barangay, company.location?.city, company.location?.province, company.location?.region].filter(Boolean).join(", ")
    : (company.location || "—");
  // No slot count on the card on purpose — slots are per department now, and a
  // single combined number here says nothing about the department a reader
  // cares about. The per-department counts live on the profile instead.
  const postedDate = company.createdAt?.seconds
    ? new Date(company.createdAt.seconds * 1000).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : (company.posted || "");

  // Expiration is worth seeing before opening a post — an expired one can't be
  // applied to. Posts with no expirationDate simply don't show the line.
  const expired = postExpired;
  const expiryDate = formatDateStr(company.expirationDate);

  const meta = { fontFamily: font.ui, ...type.helper, color: inkMuted };

  return (
    <div
      className="stud-card"
      data-closed={isActive ? undefined : "true"}
      onClick={() => isActive && onViewProfile(company)}
      style={{
        background: isActive ? surface : color.wine800,
        borderRadius: radius.card,
        border: `1px solid ${line}`,
        padding: "20px 22px",
        display: "flex", flexDirection: "column", gap: "6px",
        boxShadow: isActive ? shadow.input : "none",
        opacity: isActive ? 1 : 0.72,
        transition: `box-shadow 220ms ${ease}, border-color 220ms ${ease}`,
        cursor: isActive ? "pointer" : "default",
        position: "relative",
        minWidth: 0, overflow: "hidden",
      }}
      onMouseEnter={e => { if (isActive) { e.currentTarget.style.boxShadow = "0 10px 28px rgba(10,10,10,0.10)"; e.currentTarget.style.borderColor = color.hoverBorder; } }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = isActive ? shadow.input : "none"; e.currentTarget.style.borderColor = line; }}
    >
      <h3 style={{ fontFamily: font.ui, fontSize: "1.0625rem", fontWeight: 600, letterSpacing: "-0.01em", color: isActive ? ink : inkMuted, lineHeight: 1.3, margin: 0 }}>{displayName}</h3>
      <p style={meta}>{displayIndustry}</p>
      <p style={{ ...meta, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{displayLocation}</p>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "10px", paddingTop: "10px", borderTop: `1px solid ${lineSoft}` }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
          <span style={{ ...meta, color: inkFaint }}>{postedDate ? `Posted ${postedDate}` : ""}</span>
          {expiryDate && (
            <span style={{ ...meta, color: expired ? danger : inkFaint, fontWeight: expired ? 500 : 400 }}>
              {expired ? `Expired ${expiryDate}` : `Open until ${expiryDate}`}
            </span>
          )}
        </div>
        <span
          onClick={() => isActive && onViewProfile(company)}
          style={{ fontFamily: font.ui, ...type.helper, fontWeight: 500, color: isActive ? ink : inkFaint, cursor: isActive ? "pointer" : "default", flexShrink: 0 }}
        >
          {postExpired ? "Closed" : "View post"}
        </span>
      </div>
    </div>
  );
};

// ─── MAIN FIND COMPANY SCREEN ─────────────────────────────────────────────────
// ── Pagination: 10 rows per page ──────────────────────────────────────────────
const PAGE_SIZE = 10;
// The header (title, search, filters) is fixed; only the list under it scrolls, with its own scrollbar.
// The list box reaches into the screen padding so its scrollbar sits at the edge.
const PIN_PAD = "clamp(16px, 4vw, 28px)";
const PIN_PAD_X = "clamp(16px, 4vw, 32px)";
const fixedHead = { flexShrink: 0, position: "relative", zIndex: 30 };
const listScroll = { flex: "1 1 auto", minHeight: 0, overflowY: "auto", margin: `0 calc(-1 * ${PIN_PAD_X}) calc(-1 * ${PIN_PAD})`, padding: `0 ${PIN_PAD_X} ${PIN_PAD}` };
// Next/previous page: the list goes back to its start.
const scrollListToTop = (scroller) => { if (scroller) scroller.scrollTo({ top: 0, behavior: "smooth" }); };
// « ‹ 1 2 3 … 9 › » — same look as the Admin screens: current-page badge + page buttons in one rounded bar.
const PaginationBar = ({ page: current, totalPages, onPage, from, to, total }) => {
  const range = (a, b) => Array.from({ length: b - a + 1 }, (_, i) => a + i);
  const items = totalPages <= 7 ? range(1, totalPages)
    : current <= 4 ? [...range(1, 5), "gap-end", totalPages]
    : current >= totalPages - 3 ? [1, "gap-start", ...range(totalPages - 4, totalPages)]
    : [1, "gap-start", current - 1, current, current + 1, "gap-end", totalPages];
  const btn = (on, disabled) => ({
    minWidth: "36px", height: "36px", padding: "0 10px", borderRadius: "10px",
    border: `1.5px solid ${on ? color.blush50 : color.wine700}`, background: on ? color.blush50 : color.white, color: on ? color.white : color.ink,
    fontFamily: font.ui, fontSize: "0.82rem", fontWeight: 600, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.45 : 1,
  });
  return (
    <div style={{ display: "flex", flexWrap: "wrap-reverse", alignItems: "center", justifyContent: total != null ? "space-between" : "center", gap: "12px", padding: "20px 0 4px" }}>
      {total != null && <span style={{ fontFamily: font.ui, fontSize: "0.8rem", color: color.inkMuted }}>Showing {from}–{to} of {total}</span>}
      <nav aria-label="Pagination" style={{ marginLeft: "auto", display: "inline-flex", alignItems: "stretch", maxWidth: "100%", background: color.white, border: `1px solid ${color.wine700}`, borderRadius: "16px", boxShadow: "0 4px 16px rgba(20,20,20,0.06)", overflow: "hidden" }}>
        <div title="Current page" style={{ display: "flex", alignItems: "center", justifyContent: "center", minWidth: "52px", padding: "0 14px", background: color.wine800, borderRight: `1px solid ${color.wine700}`, fontFamily: font.ui, fontSize: "0.9rem", fontWeight: 700, color: color.ink }}>
          {String(current).padStart(2, "0")}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "6px", padding: "10px 12px", flexWrap: "wrap", justifyContent: "center" }}>
          <button type="button" aria-label="First page" title="First page" style={btn(false, current === 1)} disabled={current === 1} onClick={() => onPage(1)}>«</button>
          <button type="button" aria-label="Previous page" title="Previous page" style={btn(false, current === 1)} disabled={current === 1} onClick={() => onPage(current - 1)}>‹</button>
          {items.map((it) => (typeof it === "number"
            ? <button key={it} type="button" style={btn(it === current, false)} aria-current={it === current ? "page" : undefined} onClick={() => onPage(it)}>{it}</button>
            : <span key={it} style={{ color: color.inkMuted, padding: "0 4px" }}>…</span>))}
          <button type="button" aria-label="Next page" title="Next page" style={btn(false, current === totalPages)} disabled={current === totalPages} onClick={() => onPage(current + 1)}>›</button>
          <button type="button" aria-label="Last page" title="Last page" style={btn(false, current === totalPages)} disabled={current === totalPages} onClick={() => onPage(totalPages)}>»</button>
        </div>
      </nav>
    </div>
  );
};

const StudentFindCompanyScreen = ({ onReportSubmit, onNavigateToReports, onNavigateToApplications, onMessageNow, onApplyNow, initialCompanyId, onClearInitialCompany, user, onVisitCompany, onViewChange }) => {
  const { posts: companies, loading: postsLoading } = useOjtPosts();

  // The company of the student's confirmed OJT placement, if any — Apply Now
  // opens an info modal instead of the apply form once the student has confirmed where they'll be deployed.
  const [confirmedPlacement, setConfirmedPlacement] = useState(null);
  useEffect(() => {
    if (!user?.uid) return;
    const q = query(collection(db, "applications"), where("studentId", "==", user.uid));
    let stale = false;
    const unsub = onSnapshot(q, async snap => {
      const confirmed = snap.docs.map(d => d.data()).filter(a => a.status === "Accepted" && a.placementConfirmed === true);
      // A placement at a BLOCKED company can't go ahead — don't lock the student
      // out of applying elsewhere. (Suspended still counts: it comes back.)
      let live = null;
      for (const a of confirmed) {
        let blocked = false;
        if (a.companyId) {
          try {
            const cs = await getDoc(doc(db, "companies", a.companyId));
            blocked = standingOf(cs.exists() ? cs.data() : null, "company").state === "blocked";
          } catch (err) {
            console.error("Failed to check the placement company's standing:", err);
          }
        }
        if (!blocked) { live = a; break; }
      }
      if (!stale) setConfirmedPlacement(live ? (live.companyName || "a company") : null);
    }, err => console.error("Failed to check confirmed placement:", err));
    return () => { stale = true; unsub(); };
  }, [user?.uid]);
  const loading = postsLoading || !user; // after a refresh `user` is null for a moment
  const [view, setView] = useState("list");
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [search, setSearch] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [selectedIndustries, setSelectedIndustries] = useState([]);
  const [citySearch, setCitySearch] = useState("");
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [showPlacementModal, setShowPlacementModal] = useState(false);
  const filterRef = useRef(null);
  const [curPage, setCurPage] = useState(1);
  // Next/previous page: the list goes back to its start.
  const scrollRef = useRef(null);
  const goToPage = (n) => { setCurPage(n); requestAnimationFrame(() => scrollListToTop(scrollRef.current)); };

  // Tells the Dashboard which part of Find Company is showing, so its "?"
  // help button (and first-visit auto-tour) runs the matching steps:
  // "list" | "profile" | "report" | "apply". Reset to "list" on unmount.
  const subView = view === "profile" && selectedCompany
    ? (showApplyModal ? "apply" : showReportModal ? "report" : "profile")
    : "list";
  useEffect(() => { onViewChange?.(subView); }, [subView]);
  useEffect(() => () => onViewChange?.("list"), []);

  useEffect(() => {
    const handler = (e) => { if (filterRef.current && !filterRef.current.contains(e.target)) setShowFilter(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (initialCompanyId && companies.length > 0) {
      const company = companies.find(c => c.id === initialCompanyId);
      // An expired post can't be opened, deep link or not (the card is already
      // "Closed" in the list).
      if (company && !isPostExpired(company)) { setSelectedCompany(company); setView("profile"); onVisitCompany?.({ id: company.id, name: company.companyName || company.name }); }
      onClearInitialCompany?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCompanyId, companies]);

  const hasFilter = selectedIndustries.length > 0 || citySearch.trim();

  // Only show posts looking for students from this student's own College
  // AND Program — same scoping used in CoordinatorCompanyListScreen /
  // CoordinatorFindCompanyScreen. Department is always required; Program is
  // only required to match when BOTH the student has one set AND the post
  // carries per-Program detail (`courseSelections` — the only place a
  // post's Program actually lives; the newer flat `departments` field is
  // department-only, so those posts still match on Department alone rather
  // than disappearing for lack of Program data).
  // College/Program strings are written by several different screens, so they
  // drift in cosmetic ways — em dash vs hyphen, double spaces, casing, a stray
  // trailing space. Exact === made a post vanish for one of those differences.
  // Compare on a normalised form instead: still equality, just not brittle.
  const normalizeScope = (v) => String(v || "")
    .replace(/[\u2010-\u2015]/g, "-")   // ‐ ‑ ‒ – — ― all become a plain hyphen
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

  // Different screens have written the student's scope under different field
  // names over time; read every one that has been used rather than showing an
  // empty list because the value sits under `department` instead of `college`.
  const myCollege = user?.college || user?.department || user?.collegeName || "";
  const myProgram = user?.program || user?.course || "";
  const myCollegeKey = normalizeScope(myCollege);
  const myProgramKey = normalizeScope(myProgram);

  const getPostScopes = (c) => {
    if (Array.isArray(c.courseSelections) && c.courseSelections.length) {
      return c.courseSelections
        .filter(s => s.college)
        .map(s => ({ department: s.college, program: s.program || "" }));
    }
    if (Array.isArray(c.departments) && c.departments.length) {
      return c.departments.map(d => ({ department: d, program: "" }));
    }
    return [];
  };

  const inScopeCompanies = React.useMemo(() => {
    if (!myCollegeKey) return [];
    return companies.filter(c =>
      getPostScopes(c).some(p => {
        const postDept    = normalizeScope(p.department);
        const postProgram = normalizeScope(p.program);
        // Department always has to match. Program only has to match when both
        // sides actually name one — same rule the coordinator screen uses.
        return postDept === myCollegeKey
          && (!myProgramKey || !postProgram || postProgram === myProgramKey);
      })
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies, myCollegeKey, myProgramKey]);

  // Filter options come from the posts this viewer can actually see, so every
  // suggestion in the panel has at least one post behind it.
  const industryOptions = React.useMemo(() => buildIndustryOptions(inScopeCompanies), [inScopeCompanies]);
  const selectedIndustriesLower = selectedIndustries.map(i => i.toLowerCase());

  const filtered = inScopeCompanies.filter(c => {
    const name = (c.companyName || c.company || c.name || "").toLowerCase();
    const industryArr = industriesOf(c);
    const locObj = (c.location && typeof c.location === "object") ? c.location : {};
    // Full location text = everything actually shown on the profile's Location section
    // (barangay, city, province, region, or a full formatted address), so searching
    // any part of what the user sees — not just the city — will find it.
    const fullLocationText = [
      c.postLocation?.address,
      locObj.fullAddress,
      locObj.street,
      locObj.barangay,
      locObj.city,
      locObj.province,
      locObj.region,
      typeof c.location === "string" ? c.location : null,
    ].filter(Boolean).join(", ").toLowerCase();
    // The search box matches the company NAME only. Industry and location have
    // their own filters (FilterPanel), so they are not searched here.
    const matchSearch   = !search.trim() || name.includes(search.trim().toLowerCase());
    const matchIndustry = selectedIndustries.length === 0 || industryArr.some(ind => selectedIndustriesLower.includes(ind.toLowerCase()));
    const matchCity     = !citySearch.trim() || fullLocationText.includes(citySearch.trim().toLowerCase());
    return matchSearch && matchIndustry && matchCity;
  });

  useEffect(() => { setCurPage(1); }, [search, selectedIndustries, citySearch]);
  // ── Pagination (10 per page) ──
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(curPage, totalPages);
  const pageRows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const showingFrom = filtered.length ? (safePage - 1) * PAGE_SIZE + 1 : 0;
  const showingTo = Math.min(safePage * PAGE_SIZE, filtered.length);

  const activeBadgeLabel = () => citySearch.trim() ? citySearch.trim() : null;
  const clearAllFilters = () => { setSelectedIndustries([]); setCitySearch(""); };

  const handleReportSubmit = (report) => {
    onReportSubmit?.(report);
    setShowReportModal(false);
    setShowSuccessModal(true);
  };

  const chipStyle = { background: surface, color: inkBody, border: `1px solid ${line}`, borderRadius: radius.pill, padding: "4px 12px", fontFamily: font.ui, ...type.helper, display: "flex", alignItems: "center", gap: "6px" };

  if (view === "profile" && selectedCompany) {
    return (
      <>
        <ResponsiveStyles />
        <CompanyProfile
          company={selectedCompany}
          onBack={() => setView("list")}
          onReport={() => setShowReportModal(true)}
          onMessageNow={() => onMessageNow?.({ ...selectedCompany, fromMessageNow: true })}
          onApplyNow={() => {
            if (confirmedPlacement) { setShowPlacementModal(true); return; }
            onApplyNow?.(selectedCompany);
            setShowApplyModal(true);
          }}
        />
        {showPlacementModal && (
          <PlacementConfirmedModal company={confirmedPlacement} onClose={() => setShowPlacementModal(false)} />
        )}
        {showReportModal && (
          <ReportModal company={selectedCompany} onClose={() => setShowReportModal(false)} onSubmit={handleReportSubmit} reporter={user} />
        )}
        {showSuccessModal && (
          <SuccessModal onClose={() => { setShowSuccessModal(false); onNavigateToReports?.(); }} />
        )}
        {showApplyModal && (
          <ApplyModal
            company={selectedCompany}
            user={user}
            onClose={() => setShowApplyModal(false)}
            onSuccessClose={() => { setShowApplyModal(false); onNavigateToApplications?.(); }}
            onSubmit={() => {
              // ApplyModal already handles the full Firestore submission (incl. file
              // uploads) internally and shows its own success popup. This callback is
              // just a notification hook — do NOT close the modal here, or the success
              // popup will be unmounted before the student can see/dismiss it.
            }}
          />
        )}
      </>
    );
  }

  return (
    <>
      <ResponsiveStyles />
      {/* Outer: vertical scroll only, no horizontal overflow */}
      <div className="stud-list-wrapper" style={{ padding: "clamp(16px, 4vw, 28px) clamp(16px, 4vw, 32px)", overflow: "hidden", display: "flex", flexDirection: "column", minHeight: 0, flex: 1, background: page }}>

        <div style={fixedHead}>

        {/* Search + Filter bar */}
        <div className="stud-search-bar" style={{ background: panel, borderRadius: radius.panel }}>
          <div style={{ minWidth: 0, flex: "1 1 auto" }}>
            <span title="Find Company" style={{ fontFamily: font.ui, fontSize: "clamp(1.1rem, 3.5vw, 1.375rem)", fontWeight: 600, letterSpacing: "-0.01em", color: onPanel, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Find Company</span>
            {!loading && (
              <p title={`${filtered.length} open ${filtered.length === 1 ? "post" : "posts"} for your program`} style={{ fontFamily: font.ui, ...type.helper, color: onPanelDim, marginTop: "2px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {filtered.length} open {filtered.length === 1 ? "post" : "posts"} for your program
              </p>
            )}
          </div>
          <div style={{ position: "relative", display: "flex", alignItems: "center", flexShrink: 0 }}>
            <div id="sfind-search-bar" style={{ display: "flex", alignItems: "center", gap: space.sm, background: color.white, borderRadius: radius.pill, padding: "9px 16px" }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={inkMuted} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                id="sfind-search"
                placeholder="Search company"
                className="stud-search-input"
                style={{ border: "none", background: "transparent", outline: "none", color: ink, fontFamily: font.ui, ...type.control }}
              />
              {search && <button onClick={() => setSearch("")} aria-label="Clear search" style={{ background: "none", border: "none", color: inkMuted, cursor: "pointer", fontSize: "0.9rem", padding: 0, lineHeight: 1 }}>✕</button>}
            </div>
            <div id="sfind-filter-btn" ref={filterRef} style={{ position: "relative", marginLeft: "10px" }}>
              <div
                id="sfind-filter"
                onClick={() => setShowFilter(v => !v)}
                title="Filters"
                style={{ width: "40px", height: "40px", background: hasFilter ? color.goldTint : color.white, borderRadius: radius.pill, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", border: hasFilter ? `1px solid ${color.onWineFaint}` : "none", position: "relative" }}
              >
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={hasFilter ? onPanel : inkMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
              </div>
              {showFilter && (
                <FilterPanel
                  selectedIndustries={selectedIndustries} setSelectedIndustries={setSelectedIndustries}
                  industryOptions={industryOptions}
                  citySearch={citySearch} setCitySearch={setCitySearch}
                />
              )}
            </div>
          </div>
        </div>

        {/* Active filter chips */}
        {hasFilter && (
          <div style={{ display: "flex", alignItems: "center", gap: space.sm, marginBottom: space.md, flexWrap: "wrap" }}>
            {selectedIndustries.map(ind => (
              <span key={ind} style={chipStyle}>
                {ind}<span onClick={() => setSelectedIndustries(p => p.filter(i => i !== ind))} style={{ cursor: "pointer", color: inkMuted }}>✕</span>
              </span>
            ))}
            {activeBadgeLabel() && (
              <span style={chipStyle}>
                {activeBadgeLabel()}
                <span onClick={() => setCitySearch("")} style={{ cursor: "pointer", color: inkMuted }}>✕</span>
              </span>
            )}
            <span onClick={clearAllFilters} style={{ fontFamily: font.ui, ...type.helper, color: inkMuted, cursor: "pointer", textDecoration: "underline" }}>Clear all</span>
          </div>
        )}

        </div>

        <div ref={scrollRef} style={listScroll}>
        {loading ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "72px 0" }}>
            <p style={{ fontFamily: font.ui, ...type.body, color: inkFaint }}>Loading posts…</p>
          </div>
        ) : filtered.length > 0 ? (
          <>
          {/* CSS grid: 2-col on ≥768px, 1-col below — controlled entirely by .stud-company-grid */}
          <div id="sfind-grid" className="stud-company-grid">
            {pageRows.map(c => (
              <CompanyCard
                key={c.id}
                company={c}
                onViewProfile={(company) => { setSelectedCompany(company); setView("profile"); onVisitCompany?.({ id: company.id, name: company.companyName || company.name }); }}
              />
            ))}
          </div>
          {/* Pagination: only shown when there are more than 10 posts */}
          {filtered.length > PAGE_SIZE && (
            <PaginationBar page={safePage} totalPages={totalPages} onPage={goToPage} from={showingFrom} to={showingTo} total={filtered.length} />
          )}
          </>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "72px 24px", gap: space.sm, textAlign: "center", background: surface, border: `1px dashed ${color.wine400}`, borderRadius: radius.panel }}>
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke={inkFaint} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <p style={{ fontFamily: font.ui, fontSize: "1.0625rem", fontWeight: 600, color: ink }}>
              {!myCollege ? "Your college isn't set yet" : "No posts available"}
            </p>
            <p style={{ fontFamily: font.ui, ...type.helper, color: inkMuted, maxWidth: "44ch" }}>
              {!myCollege
                ? "Message your coordinator to have your college and program added to your account."
                : hasFilter
                  ? "Clear a filter or search a different city to widen the results."
                  : "New posts appear here as companies publish them for your program."}
            </p>
            {myCollege && hasFilter && (
              <button onClick={clearAllFilters} style={{ marginTop: space.sm, background: panel, color: onPanel, border: "none", borderRadius: radius.pill, padding: "9px 20px", fontFamily: font.ui, ...type.control, cursor: "pointer" }}>
                Clear filters
              </button>
            )}
          </div>
        )}
        </div>
      </div>
    </>
  );
};

export default StudentFindCompanyScreen;